import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import type { Location } from 'history';
import type { FunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { permittedIssueCategoriesToView } from '../../helpers/issuesV2';
import useHasMounted from '../../hooks/useHasMounted';
import useOnMount from '../../hooks/useOnMount';
import * as IssuesV2Store from '../../redux/issuesV2';
import ProjectGroupModule from '../../redux/project_grouping';
import { toFiniteNumber } from '../../utils';
import NotFound from '../generic/NotFound';
import { useRevisionScanIdQueryParam } from '../projects/hooks/useRevisionScanIdQueryParam';
import { RevisionStatusBanner } from '../projects/RevisionStatusBanner';
import type { Category } from '../../../shared/issuesV2';
import type { ProjectDashboardProject, ProjectDashboardRevision } from '../projects/projectDashboardTypes';

import ComparisonHeader from './IssueComparison/Header/ComparisonHeader';
import useCategorySummary from './IssuesInbox/hooks/useCategorySummary';
import useIssuesLicenseList from './IssuesInbox/hooks/useIssuesLicenseList';
import useProjectType from './IssuesInbox/hooks/useProjectType';
import useSetIssuesScope from './IssuesInbox/hooks/useSetIssuesScope';
import GlobalIssuesCallToAction from './subnavs/GlobalIssuesCallToAction';
import GlobalIssuesSubnav from './subnavs/GlobalIssuesSubnav';

//  These are the "pieces" of the URL, like /issues/:category/:id
export type IssuesContainerParams = {
  category: Category;
  //  This param is only present when viewing a single issue (ie: IssueDetails).
  id?: string;
  //  If these are set, we know that we are on the project page and need to set the scope to project.
  project_id?: string; // eslint-disable-line @typescript-eslint/naming-convention -- Param name already exists.
  revision_id?: string; // eslint-disable-line @typescript-eslint/naming-convention -- Param name already exists.
  reference_type?: string; // eslint-disable-line @typescript-eslint/naming-convention -- Param name already exists.
  reference: string;
  //  If these are set, we know that we are on the release group issues inbox and need to set the scope to releaseGroup
  projectGroupId?: string;
  //  This provided by ReleaseGroupIssuesContainer, it does not exist as a URL param.
  projectReleaseId?: number;
};

//  For projects and release groups, when a different issue scan is selected, it will exist in the query params.
//  If this is not present, we attempt to use the latest scan ID.
export type IssuesContainerQueryString = {
  revisionScanId?: string;
  releaseScanId?: string;
};

export type IssuesContainerProps = {
  params: IssuesContainerParams;
  location: Location<IssuesContainerQueryString>;
  // eslint-disable-next-line react/no-unused-prop-types -- It's not used in this component, but it exists and is used in other places we use this type.
  router: InjectedRouter;
  //  If project is undefined, it means it hasn't loaded yet.
  project?: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
};

/**
 * IssuesContainer is a top-level container component which handles
 * some things related to both the IssuesInbox and IssueDetails:
 *
 *  1. Setting the Scope correctly (ie: "is this the global, project, or release group issues page?")
 *  2. Making sure the subnav is displayed and functions correctly.
 *  3. Fetching the category summary (once), based on the scope.
 */
const IssuesContainer: FunctionComponent<IssuesContainerProps> = ({
  params,
  children,
  location,
  project,
  revision,
}) => {
  const dispatch = useDispatch();
  const hasMounted = useHasMounted();

  const { revisionScanId: revisionScanIdFromQuery } = useRevisionScanIdQueryParam();
  // Ensure we always show issues for selected revision scan id
  // or release scanId (for release groups)
  const stateReleaseScanId = useSelector(ProjectGroupModule.selectors.getSelectedScanId);

  //  Ensure that the scope is correctly set, based on the params.
  //  Returns the scope so it can be passed to the other two hooks.
  const scope = useSetIssuesScope({
    ...params,
    //  Try to use the query param first if it exists, otherwise just use whatever is in the state.
    //  This ensures that if the user hits the page directly and the scanId hasn't been selected, we still use the correct one.
    revisionScanId: toFiniteNumber(location.query.revisionScanId) || revisionScanIdFromQuery,
    releaseScanId: toFiniteNumber(location.query.releaseScanId) || stateReleaseScanId,
  });

  // Ensure we have project type set, so that filters predicated
  // on project type can be rendered.
  const projectType = project?.data?.type ?? undefined; // null is not allowed

  const isComparisonModeOn = useSelector(IssuesV2Store.selectors.getComparisonMode);

  // Sets the project type to issuesV2 store
  // so that we can optionally render layer filters
  useProjectType(scope, projectType);

  //  Handle fetching the categorySummary, which is dependent on scope.
  //  Note that this doesn't change across searches, so this fetch only needs to happen once here at the top.
  useCategorySummary(scope);

  // handle loading the license list for licensing issues
  useIssuesLicenseList();

  useOnMount(
    () => {
      //  Ensure that the category is set correctly. This is only actually required when the user lands
      //  directly on the IssueDetails page.
      dispatch(IssuesV2Store.actions.setIssueCategory(params.category));

      //  When unmounting the component, we want to reset the state
      //  to avoid stale data, like when navigating between different projects
      //  or from the global to a project issues page.
    },
    () => dispatch(IssuesV2Store.actions.resetState()),
  );

  const isOverviewPage = location.pathname.endsWith('/issues/overview');

  //  Return 404/error
  if (!isOverviewPage && !permittedIssueCategoriesToView().includes(params.category)) {
    return <NotFound />;
  }

  //  Render the child route, either IssuesInbox or IssueDetails.
  //  Note that we wait until the component mounts so that category is set correctly.
  //  This is because child components will need to access it, and on the initial mount category === initalState.category (ie: licensing).
  if (!hasMounted) {
    return null;
  }

  return (
    <>
      {scope.type === 'global' && (
        <>
          <GlobalIssuesCallToAction />
          <GlobalIssuesSubnav />
        </>
      )}
      {isComparisonModeOn && (
        <ComparisonHeader />
      )}
      <div className={cx({
        // When we're in comparison mode, we don't want the main container class because it adds padding on the left and right
        // that makes the page feel cramped.
        container: !isComparisonModeOn,
      }, 'container-responsive')}>
        <RevisionStatusBanner project={project} revision={revision} params={params} />
        {children}
      </div>
    </>
  );
};

export default IssuesContainer;
